import { useMutation, useQuery } from "vue-query"
import { useContext } from "@nuxtjs/composition-api"
import { AxiosResponse } from "axios"

import { ApiPaths, UserFileNames } from "@/enums"
import { AuthResponse } from "@/types/apiResponses/AuthResponse"
import { User } from "@/types/models/User"
import { CustomMutationHook, CustomQueryHook } from "@/types/vue-query"

export type UserId = {
  userId: User["id"]
}

export type UpdateUserEmailPayload = {
  newEmail: string
}

export type ConfirmUpdateUserEmailPayload = {
  id: number
  token: string
}

interface GetUserFileQueryParams {
  fileName: UserFileNames
}

export const useUserQuery: CustomQueryHook<UserId, User> = (
  params,
  options,
) => {
  const { $axios } = useContext()

  return useQuery(
    ["userQuery", params],
    async () => {
      const { data } = await $axios.get<User>(ApiPaths.GET_USER, {
        params: { user_id: params.userId },
      })
      return data
    },
    options,
  )
}

export const useCurrentUserQuery: CustomQueryHook<void, AxiosResponse<User>> = (
  options,
) => {
  const { $axios } = useContext()

  return useQuery(
    ["currentUserQuery"],
    async () => {
      const response = await $axios.get<User>(ApiPaths.GET_USER_ME)
      return response
    },
    { keepPreviousData: true, ...options },
  )
}

export const updateUserEmail: CustomMutationHook<
  UpdateUserEmailPayload,
  void
> = (options) => {
  const { $axios } = useContext()

  return useMutation(
    async (payload: UpdateUserEmailPayload) => {
      const { data } = await $axios.post<void>(
        ApiPaths.UPDATE_USER_EMAIL,
        payload,
      )

      return data
    },
    { ...options },
  )
}

export const confirmUpdateUserEmail: CustomMutationHook<
  ConfirmUpdateUserEmailPayload,
  AuthResponse
> = (options) => {
  const { $axios } = useContext()

  return useMutation(
    async (payload: ConfirmUpdateUserEmailPayload) => {
      const { data, headers } = await $axios.post<User>(
        ApiPaths.UPDATE_USER_EMAIL_CONFIRM,
        payload,
      )

      return { data, headers }
    },
    { ...options },
  )
}

export const updateUserFiles: CustomMutationHook<
  { fileName: UserFileNames; file: File },
  User
> = (options) => {
  const { $axios } = useContext()

  return useMutation(
    async ({ fileName, file }: { fileName: UserFileNames; file: File }) => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("field", fileName)
      const { data } = await $axios.post<User>(
        ApiPaths.UPDATE_USER_FILES,
        formData,
      )

      return data
    },
    { ...options },
  )
}

export const getUserFilesQuery: CustomQueryHook<
  GetUserFileQueryParams,
  { data: Blob; headers: any }
> = (params, options) => {
  const { $axios } = useContext()

  return useQuery(
    ["getSignUpData", params],
    async () => {
      const { data, headers } = await $axios.get<Blob>(
        ApiPaths.GET_USER_FILES,
        {
          params: {
            field: params.fileName,
          },
          responseType: "blob",
        },
      )

      return { data, headers }
    },
    { keepPreviousData: true, ...options },
  )
}
